import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import { Box } from 'localmed-core';
import { FormattedMessage } from 'react-intl';

import { historyShape } from '../../../shared/shapes';
import { universalContextShape } from '../../../_universal';
import { withCustomContexts } from '../../../shared/components/Contexts';
import { OfficeSchedulingCalendarWithData } from '../../../shared/components/SchedulingCalendar';
import { widgetSettingsShape } from '../../../shared/components/WidgetSettings';
import { createSchedulingSessionStateLandingPage } from '../../../shared/components/SchedulingSessionState';
import { WIDGET_CHROME, PATIENT_STATUS } from '../../../shared/constants';
import { trackAffiliateEvent } from '../../../shared/utils/affiliateTracking';
import getSchedulingParams from '../../../shared/utils/getSchedulingParams';
import { getQueryFromLocation } from '../../../shared/utils/urls';
import ForceSelection from '../../components/ForceSelection';
import WidgetForm from '../../components/WidgetForm';
import WidgetLayout from '../../components/WidgetLayout';
import WidgetOfficeHeader from '../../components/WidgetOfficeHeader';
import WidgetRatings from '../../components/WidgetRatings';
import { VIEW_OPENINGS_BY } from './constants';
import NotFoundPage from '../NotFoundPage';
import LoadingPage from '../LoadingPage';
import OfficeProviderListWithData from './OfficeProviderListWithData';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import PatientScreener, {
  PatientScreenerFailure,
  PatientScreenerSuccess,
  SCREENER_STATUSES,
} from '../../../shared/components/PatientScreener';
import WidgetSessionOpenedMutation from '../../components/WidgetSessionOpenedMutation';

class OfficeSchedulingPage extends React.Component {
  static propTypes = {
    history: historyShape,
    data: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      office: PropTypes.shape({
        id: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
        phone: PropTypes.string.isRequired,
        viewOpeningsBy: PropTypes.arrayOf(PropTypes.string),
        onlineSchedulingEnabled: PropTypes.bool,
        practice: PropTypes.shape({
          display: PropTypes.string.isRequired,
        }).isRequired,
        reasonsForVisit: PropTypes.shape({
          nodes: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
            })
          ),
        }),
        reviewsEnabled: PropTypes.bool,
        localmedRating: PropTypes.object,
        facebookRating: PropTypes.object,
        googleRating: PropTypes.object,
        yelpRating: PropTypes.object,
        announcementEnabled: PropTypes.bool,
        patientScreenerEnabled: PropTypes.bool,
        announcement: PropTypes.shape({
          type: PropTypes.string,
          content: PropTypes.string.isRequired,
          moreUrl: PropTypes.string,
        }),
      }),
    }).isRequired,
    universalContext: universalContextShape.isRequired,
    widgetSettings: widgetSettingsShape.isRequired,
    patientScreenerStatus: PropTypes.string,
  };

  openedMutationCalled = false;

  constructor(props) {
    super(props);

    this.state = {
      showScreenerSuccess: false,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      data: { office },
      patientScreenerStatus,
    } = this.props;
    if (
      !office ||
      !office.patientScreenerEnabled ||
      prevProps.patientScreenerStatus === patientScreenerStatus
    )
      return;
    if (patientScreenerStatus === SCREENER_STATUSES.PASSED) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ showScreenerSuccess: true });
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (this.tracked) {
      return;
    }
    const {
      data: { loading, office },
      match: { params },
    } = nextProps;
    const { widgetSettings } = this.props;

    if (loading) {
      return;
    }
    const { trackEvent } = this.props.universalContext;
    if (office) {
      trackEvent({
        event: 'Viewed Office',
        properties: {
          officeId: office.id,
          officeDisplay: office.display,
          practiceId: office.practice.id,
          practiceDisplay: office.practice.display,
          reasonForVisitCount: office.reasonsForVisit.nodes.length,
          viewOpeningsBy: office.viewOpeningsBy && office.viewOpeningsBy[0],
          widget: true,
        },
      });
    } else {
      trackEvent({
        event: 'Office Widget Not Found',
        properties: { officeId: params.id },
      });
    }
    trackEvent({ event: 'Viewed Widget Version 2' });
    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      event: 'Viewed LocalMed Widget',
    });

    this.tracked = true;
  }

  closeScreenerSuccess = () => {
    this.setState({ showScreenerSuccess: false });
  };

  setOpenedCalled = (value) => {
    this.openedMutationCalled = value;
  };

  render() {
    const {
      history,
      data: { loading, office },
      patientScreenerStatus,
    } = this.props;
    const { trackerData, widgetSettings: { chrome } = {} } = this.props;

    const query = getQueryFromLocation(history.location);
    if (loading) return <LoadingPage />;

    if (!office) return <NotFoundPage />;
    const { announcementEnabled, announcement, patientScreenerEnabled } = office;

    const schedulingParams = getSchedulingParams({
      history,
      reasonsForVisit: office.reasonsForVisit.nodes,
    });

    const noOnlineScheduling = !office.onlineSchedulingEnabled;

    const { reasonForVisit, reasonsForVisit, patientStatus } = schedulingParams.values;
    const viewOpeningsByOffice =
      office.viewOpeningsBy && office.viewOpeningsBy.indexOf(VIEW_OPENINGS_BY.OFFICE) !== -1;

    // Update Display message based on patient status and available reasons for that status
    const patientStatusItems = [
      { id: PATIENT_STATUS.NEW, display: 'New Patient' },
      { id: PATIENT_STATUS.RETURNING, display: 'Returning Patient' },
    ];
    const selectedPatientStatus =
      patientStatusItems.find((p) => p.id === patientStatus) || patientStatusItems[0];
    const reasonForVisitItems = reasonsForVisit.filter(
      (r) => r.patientStatus === selectedPatientStatus.id
    );

    const noOnlineSchedulingForStatus = reasonForVisitItems.length === 0;
    const showScheduling =
      patientScreenerStatus === SCREENER_STATUSES.PASSED || !patientScreenerEnabled;

    return (
      <WidgetLayout
        header={<WidgetOfficeHeader office={office} />}
        isEmbedded={chrome === WIDGET_CHROME.EMBEDDED}
      >
        <Helmet>
          <title>{office.practice.display}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <WidgetSessionOpenedMutation
          widgetId={trackerData?.widgetId}
          setCalled={this.setOpenedCalled}
          called={this.openedMutationCalled}
        />
        {announcementEnabled && <AnnouncementBanner announcement={announcement} />}
        {showScheduling ? (
          <Fragment>
            {this.state.showScreenerSuccess && (
              <PatientScreenerSuccess onClose={this.closeScreenerSuccess} />
            )}
            {office.reviewsEnabled && (
              <WidgetRatings
                ratings={[
                  office.localmedRating,
                  office.googleRating,
                  office.facebookRating,
                  office.yelpRating,
                ]}
              />
            )}
            {reasonsForVisit.length === 0 || noOnlineScheduling ? (
              <div className="padding-sm text-align-center">
                <h2 className="font-family-meta-bold font-size-lg line-height-solid margin-top-lg margin-bottom-xs">
                  We‘re not accepting appointments online at the moment.
                </h2>
                <p>
                  Please call us at{' '}
                  <span className="text-nowrap font-family-meta-bold">{office.phone}</span> to
                  schedule your appointment.
                </p>
              </div>
            ) : (
              <Fragment key="WidgetForm">
                <WidgetForm
                  reasonForVisit={reasonForVisit}
                  reasonForVisitItems={reasonForVisitItems}
                  selectedPatientStatus={selectedPatientStatus}
                  noOnlineSchedulingForStatus={noOnlineSchedulingForStatus}
                  onChangePatientStatus={schedulingParams.updatePatientStatus}
                  onChangeReasonForVisit={schedulingParams.updateReasonForVisit}
                />

                {noOnlineSchedulingForStatus ? (
                  <div className="padding-sm text-align-center" key="noOnlineSchedulingForStatus">
                    <h2 className="font-family-meta-bold font-size-lg line-height-solid margin-top-lg margin-bottom-xs">
                      <FormattedMessage
                        id="OfficeSchedulingPage.notAcceptingHeader"
                        defaultMessage="Please call us at {phoneNumber} to schedule your appointment."
                        values={{
                          phoneNumber: (
                            <a href={`tel:${office.phone}`} className="text-nowrap">
                              {office.phone}
                            </a>
                          ),
                        }}
                      />
                    </h2>
                    <p className="notAccepting">
                      <FormattedMessage
                        id="OfficeSchedulingPage.notAcceptingBody"
                        defaultMessage="We’re not accepting {patientStatus, select, NEW {new} RETURNING {returning}} patients online
                        at the moment."
                        values={{
                          patientStatus: selectedPatientStatus.id,
                        }}
                      />
                    </p>
                  </div>
                ) : (
                  <ForceSelection selected={reasonForVisit != null}>
                    {reasonForVisit != null &&
                      (viewOpeningsByOffice ? (
                        <OfficeSchedulingCalendarWithData
                          office={office}
                          reasonForVisit={reasonForVisit}
                          selectedDate={schedulingParams.values.selectedDate}
                          minDate={schedulingParams.values.minDate}
                          onChangeSelectedDate={schedulingParams.updateSelectedDate}
                          isOnWidget
                          mt="sm"
                        />
                      ) : (
                        <OfficeProviderListWithData
                          office={office}
                          reasonForVisit={reasonForVisit}
                          specialtyId={query.specialtyId}
                          after={schedulingParams.values.minDate}
                        />
                      ))}
                  </ForceSelection>
                )}
              </Fragment>
            )}
          </Fragment>
        ) : (
          <Box maxWidth="720px" m="0 auto">
            {patientScreenerStatus === SCREENER_STATUSES.NOT_SCREENED && (
              <PatientScreener officeId={office.id} officeDisplay={office.display} widget />
            )}
            {patientScreenerStatus === SCREENER_STATUSES.FAILED && (
              <PatientScreenerFailure phoneNumber={office.phone} />
            )}
          </Box>
        )}
      </WidgetLayout>
    );
  }
}

export default createSchedulingSessionStateLandingPage(withCustomContexts(OfficeSchedulingPage));
